import { TrendingUp, TrendingDown, Minus, RotateCcw, Zap, Activity, Shield, Brain, BarChart2, Clock, Calendar } from 'lucide-react';

// ── Gap thresholds — single source of truth for all systems ──────────────────
// Import these in backend services and ACT to avoid divergence.
export const GAP_THRESHOLDS = {
  strong_up:   0.8,   // > +0.8%
  mild_up:     0.2,   // +0.2% to +0.8%
  flat:        0.2,   // within ±0.2%
  mild_down:   0.2,   // -0.2% to -0.8%
  strong_down: 0.8,   // < -0.8%
};

// ── VIX levels — single source of truth ──────────────────────────────────────
export const VIX_LEVELS = {
  normal:   { max: 15,  label: 'Normal',       color: '#16C784', riskMod: 1.0,  note: 'Normal conditions. No size adjustment needed.' },
  elevated: { max: 20,  label: 'Elevated',     color: '#f59e0b', riskMod: 0.75, note: 'VIX 15–20: slightly elevated. Reduce size by 25%.' },
  high:     { max: 25,  label: 'High',         color: '#f97316', riskMod: 0.5,  note: 'VIX 20–25: high volatility. Half size only.' },
  extreme:  { max: 999, label: 'Extreme',      color: '#ef4444', riskMod: 0.25, note: 'VIX above 25: extreme. Minimum size or sit out.' },
};

export function classifyVix(vix) {
  if (!vix || vix <= 0) return null;
  if (vix <= 15) return { ...VIX_LEVELS.normal,   vix };
  if (vix <= 20) return { ...VIX_LEVELS.elevated,  vix };
  if (vix <= 25) return { ...VIX_LEVELS.high,       vix };
  return           { ...VIX_LEVELS.extreme,          vix };
}

// ── Step 1: Global Context ────────────────────────────────────────────────────
export const GLOBAL_CONTEXTS = [
  {
    id: 'green', label: 'Markets Green', color: '#16C784', icon: TrendingUp,
    sub: 'US / Europe / Asia closed positive. SGX Nifty indicating gap up.',
    riskMod: 1.0, bias: 'bull',
    why: 'Positive global cues give domestic bulls a tailwind. Institutions are more likely to buy early. Risk appetite is higher.',
  },
  {
    id: 'mixed', label: 'Mixed Signals', color: '#f59e0b', icon: Minus,
    sub: 'Some markets positive, some negative. No clear global direction.',
    riskMod: 0.75, bias: 'neutral',
    why: 'Conflicting global data means domestic factors will drive Nifty. No clear macro edge. Sector rotation likely. Wait for a domestic setup to emerge.',
  },
  {
    id: 'red', label: 'Markets Red', color: '#ef4444', icon: TrendingDown,
    sub: 'US / Europe / Asia closed negative. SGX Nifty indicating gap down.',
    riskMod: 0.5, bias: 'bear',
    why: 'Negative global cues increase gap-down risk and early selling pressure. Even if Nifty recovers, the overhead supply is heavier. Reduce size.',
  },
];

// ── Step 1b: FII Stance (part of global context, checked alongside Step 1) ───
// FII data is known the night before — it is a pre-market input, not intraday.
export const FII_CONTEXTS = [
  {
    id: 'buying',  label: 'FII Buying',  color: '#16C784', icon: TrendingUp,
    sub: 'Net positive flow — institutions are accumulating. Bullish tailwind.',
    riskMod: 1.0, bias: 'buy',
    why: 'FIIs are net buyers. Institutional demand gives a structural tailwind. Long setups have higher follow-through probability.',
  },
  {
    id: 'neutral', label: 'FII Neutral', color: '#f59e0b', icon: Minus,
    sub: 'Flow is flat or data unavailable. DIIs will be the swing factor.',
    riskMod: 0.9, bias: 'neutral',
    why: 'FII flow is flat or unavailable. Domestic institutions (DIIs) will be the swing factor. No clear institutional edge.',
  },
  {
    id: 'selling', label: 'FII Selling', color: '#ef4444', icon: TrendingDown,
    sub: 'Net negative flow — institutions distributing. Overhead supply risk.',
    riskMod: 0.75, bias: 'sell',
    why: 'FIIs are net sellers. Even if global cues are green, sustained FII selling creates overhead supply. Reduce size and avoid chasing breakouts.',
  },
];

// ── Step 3: Expiry Day ────────────────────────────────────────────────────────
// Thursday = Nifty weekly expiry. Changes option premium dynamics entirely.
export const EXPIRY_CONTEXTS = [
  {
    id: 'no',
    label: 'Not Expiry Day',
    sub: 'Normal trading day — standard playbook applies.',
    color: '#4ecca3',
    icon: Calendar,
    riskMod: 1.0,
    isExpiry: false,
    why: 'Normal session. No expiry-driven premium collapse. Standard setup and instrument rules apply.',
    note: null,
  },
  {
    id: 'yes',
    label: 'Expiry Day (Nifty)',
    sub: 'Nifty weekly expiry (Tuesday) — premium collapses fast, CE/PE buying is dangerous.',
    color: '#f97316',
    icon: Calendar,
    riskMod: 0.85,
    isExpiry: true,
    why: 'On expiry day, option premiums decay rapidly even if price moves in your direction. CE/PE buying is almost always a losing strategy. Option selling (on OTM strikes outside expected range) and scalping with tight SL are the dominant plays. Avoid buying options unless the move is fast and large.',
    note: 'CE/PE buying is high-risk on expiry. Option selling and quick scalps are preferred.',
  },
];

// ── Step 4: Time of Day ───────────────────────────────────────────────────────
// When you are planning to trade changes everything — volatility, spreads, and
// which setups are valid differ dramatically across the session.
export const TIME_SLOTS = [
  {
    id: 'opening',
    label: 'Opening Play',
    sub: '9:15 – 9:45 AM — highest volatility, gap behavior decides direction.',
    color: '#ef4444',
    icon: Clock,
    riskMod: 0.9,
    why: 'The first 30 minutes are driven by overnight news, gap reactions, and institutional order flow. Spreads are wide, stops get hunted, and the direction is often false. Best action: observe for the first 15 minutes, then enter only if the gap behavior is clear (sustain or gap fill). Do not trade reversals at open.',
    note: 'Wait at least until 9:30 AM before entering. The 9:15–9:30 window is institutional order absorption — not retail entry time.',
    bestSetups: ['breakout', 'orb_breakout', 'gap_fill'],
    avoidSetups: ['reversal', 'vwap_reclaim'],
  },
  {
    id: 'mid_morning',
    label: 'Mid-Morning',
    sub: '9:45 – 11:30 AM — trends established, cleanest entry window.',
    color: '#16C784',
    icon: Clock,
    riskMod: 1.0,
    why: 'This is the best trading window of the day. Institutional direction is confirmed, spreads have tightened, and setups have had time to develop properly. Pullback entries in established trends work well here. ORB breakouts often trigger in this window on flat open days.',
    note: 'Highest probability window. Trend days make most of their move between 9:45 and 11:30.',
    bestSetups: ['pullback', 'orb_breakout', 'breakout', 'vwap_reclaim'],
    avoidSetups: ['none'],
  },
  {
    id: 'lunch_chop',
    label: 'Lunch Chop',
    sub: '11:30 AM – 1:30 PM — low volume, choppy, most setups fail.',
    color: '#f59e0b',
    icon: Clock,
    riskMod: 0.5,
    why: 'Institutional desks go quiet during lunch. Volume drops sharply, spreads widen, and price moves become random and whipsaw-driven. Breakouts fail more often. Option premiums decay faster. This is the hardest window to trade profitably. If you are not already in a position from the morning, the best play is to wait.',
    note: 'Avoid new entries in this window. If already in a trade, hold if trend is intact, exit if choppy.',
    bestSetups: ['none', 'option_sell'],
    avoidSetups: ['breakout', 'orb_breakout', 'reversal', 'ce_buy', 'pe_buy'],
  },
  {
    id: 'power_hour',
    label: 'Power Hour',
    sub: '2:00 – 3:20 PM — institutional unwinding, trend resumption or reversals.',
    color: '#8b5cf6',
    icon: Clock,
    riskMod: 0.85,
    why: 'Volume picks back up as institutions begin squaring positions before close. The afternoon trend often either resumes the morning direction or produces a sharp reversal as F&O positions get unwound. Good for catching a final trend leg or option selling with same-day expiry premium. Exit all intraday positions by 3:15 PM.',
    note: 'Hard stop: exit everything by 3:15 PM. Last 10 minutes are dangerous — institutional algo squaring creates violent moves.',
    bestSetups: ['pullback', 'reversal', 'option_sell'],
    avoidSetups: ['orb_breakout'],
  },
];

// ── Step 5: Nifty Gap ─────────────────────────────────────────────────────────
export const NIFTY_GAPS = [
  {
    id: 'strong_up',   label: 'Strong Gap Up',   color: '#16C784', icon: TrendingUp,
    riskMod: 0.85, bias: 'bull',
    caution: 'Do NOT chase the open. High gap-fill probability in first 30 mins.',
    why: 'Large gap ups create a trapped-buyer risk in the first 15 minutes. The open is driven by overnight news — not intraday buyers. Smart money often sells into the euphoria.',
    threshold: `> +${GAP_THRESHOLDS.strong_up}%`,
  },
  {
    id: 'mild_up',     label: 'Mild Gap Up',     color: '#4ecca3', icon: TrendingUp,
    riskMod: 1.0, bias: 'bull',
    caution: 'Confirm the gap holds in the first 15 minutes before entering.',
    why: 'Mild gap ups are the cleanest bullish scenario. Enough positive bias to give bulls an edge, without the trap risk of a strong gap.',
    threshold: `+${GAP_THRESHOLDS.flat}% to +${GAP_THRESHOLDS.strong_up}%`,
  },
  {
    id: 'flat',        label: 'Flat Open',        color: '#f59e0b', icon: Minus,
    riskMod: 1.0, bias: 'neutral',
    caution: 'Do not predict direction before 9:45 AM. Let the range form first.',
    why: 'Flat opens mean no overnight conviction. Direction will be decided purely by intraday order flow. This is an ORB day — wait for a volume breakout of the opening range.',
    threshold: `within ±${GAP_THRESHOLDS.flat}%`,
  },
  {
    id: 'mild_down',   label: 'Mild Gap Down',   color: '#f97316', icon: TrendingDown,
    riskMod: 0.75, bias: 'bear',
    caution: 'Watch for gap fill attempt before deciding on direction.',
    why: 'Mild gap downs often attract bargain buyers early. If global sentiment is not deeply negative, the gap frequently fills back to the previous close.',
    threshold: `-${GAP_THRESHOLDS.flat}% to -${GAP_THRESHOLDS.strong_down}%`,
  },
  {
    id: 'strong_down', label: 'Strong Gap Down', color: '#ef4444', icon: TrendingDown,
    riskMod: 0.5, bias: 'bear',
    caution: 'Reduce size to half. Wait for stabilisation. Shorting the panic flush is how retail traders lose.',
    why: 'Strong gap downs create panic at open. Weak hands exit immediately, creating a flush that smart money uses to accumulate.',
    threshold: `< -${GAP_THRESHOLDS.strong_down}%`,
  },
];

// ── Step 6: Post-Open Behaviour (only assess AFTER 9:30 AM) ─────────────────
export const OPEN_BEHAVIORS = [
  {
    id: 'sustained',  label: 'Sustained / Trending', color: '#16C784', icon: TrendingUp,
    riskMod: 1.0,
    signal: 'Trend day confirmed. Enter on pullbacks, not breakouts of open.',
    why: 'Sustained opens confirm institutional participation in the gap direction. These are the cleanest trend days.',
    timeNote: 'Assess after 9:30 AM. A sustained move before 9:30 is noise.',
  },
  {
    id: 'gap_fill',   label: 'Gap Filling Back',     color: '#f59e0b', icon: RotateCcw,
    riskMod: 0.75,
    signal: 'Wait for the fill to complete. Then re-assess direction.',
    why: 'Gap fills happen because smart money distributes at extreme gap prices. Once the fill completes, real direction often emerges. This is a tradeable setup — not a dangerous one.',
    timeNote: 'Gap fill is cleaner after 9:30 AM when panic subsides.',
  },
  {
    id: 'range',      label: 'Range / Choppy Open',  color: '#8b5cf6', icon: Minus,
    riskMod: 0.5,
    signal: 'Define the range boundaries. Wait for a volume-confirmed breakout above or below.',
    why: 'Range opens mean neither bulls nor bears are in control. This is an ORB setup — wait for a volume breakout. Do not guess direction inside the range.',
    timeNote: 'Let the range fully form until at least 9:45 AM before trading the breakout.',
  },
  {
    id: 'reversal',   label: 'Sharp Reversal',        color: '#ef4444', icon: Zap,
    riskMod: 0.25,
    signal: 'Your original bias is wrong. Reduce size or sit out entirely.',
    why: 'Sharp reversals are the most dangerous opening scenario. Your original directional bias from global cues is now likely wrong.',
    timeNote: 'A reversal before 9:30 AM may be noise. Confirm it holds direction after 9:30.',
  },
  {
    id: 'mixed',      label: 'Mixed Signals',         color: '#f59e0b', icon: Activity,
    riskMod: 0.5,
    signal: 'Macro signals conflicted — global and FII pulled in different directions. Watch price action to find the real direction.',
    why: 'When global cues are bullish but FII is selling (or vice versa), neither bulls nor bears have a clean edge. Price action and volume are the only reliable guides.',
    timeNote: 'Wait until 9:45 AM for direction to establish before taking a position.',
  },
];

// ── Step 7: Setup Quality ─────────────────────────────────────────────────────
export const SETUPS = [
  {
    id: 'breakout',     label: 'Breakout Setup',         color: '#16C784', icon: TrendingUp,
    riskMod: 1.0,
    entry:  'Enter on the RETEST of the broken level — not the initial candle.',
    sl:     'SL just below the breakout level.',
    target: 'Minimum 1.5× your SL distance.',
    when:   'Price breaks a key level (prev high, resistance, consolidation top) with volume.',
  },
  {
    id: 'orb_breakout', label: 'ORB Breakout',           color: '#4ecca3', icon: TrendingUp,
    riskMod: 1.0,
    entry:  'Enter when price closes above ORB High (or below ORB Low) with volume. Wait for the candle to close.',
    sl:     'SL at the opposite side of the opening range (ORB Low for longs, ORB High for shorts).',
    target: 'Minimum 1.5× the opening range width.',
    when:   'Flat open day. Price forms a range in first 15–30 min, then breaks with volume after 9:45 AM.',
  },
  {
    id: 'pullback',     label: 'Pullback / Retest',      color: '#4ecca3', icon: Activity,
    riskMod: 1.0,
    entry:  'Enter when the pullback shows rejection — first bullish candle bouncing from support.',
    sl:     'SL below the pullback low.',
    target: 'Previous swing high or next resistance.',
    when:   'Trending day. Price pulls back to VWAP, a prior breakout level, or a moving average.',
  },
  {
    id: 'vwap_reclaim', label: 'VWAP Reclaim',           color: '#6366f1', icon: Activity,
    riskMod: 0.85,
    entry:  'Enter when price crosses and holds above VWAP with volume. Confirm on the next candle.',
    sl:     'SL below VWAP. If price falls back below, the setup has failed.',
    target: 'First resistance above VWAP — prior high or day open.',
    when:   'Price was below VWAP for 20+ minutes, then reclaims with volume. Often signals intraday reversal.',
  },
  {
    id: 'reversal',     label: 'Reversal Setup',         color: '#f97316', icon: RotateCcw,
    riskMod: 0.5,
    entry:  'Wait for structural break (lower high formed). No entry on a single reversal candle.',
    sl:     'SL above the most recent swing high (for shorts).',
    target: 'First major support below. Partial exit early.',
    when:   'Price has made a clear extended move. Reversal candle with high volume at key level.',
  },
  {
    id: 'none',         label: 'No Clear Setup',         color: '#6b7280', icon: Brain,
    riskMod: 0,
    entry:  'No entry.',
    sl:     null,
    target: null,
    when:   'No identifiable pattern. Choppy price action with no directional structure.',
  },
];

// ── Step 8: VIX Level ─────────────────────────────────────────────────────────
export const VIX_OPTIONS = [
  {
    id: 'normal',   label: 'Normal (< 15)',      color: '#16C784', icon: Activity,
    sub: 'Low volatility — options are cheap, standard sizing applies.',
    riskMod: 1.0,  vix: 12,
    why: 'VIX below 15 means the market expects calm conditions. Options premiums are fair. Standard playbook rules apply with no size adjustment.',
  },
  {
    id: 'elevated', label: 'Elevated (15–20)',    color: '#f59e0b', icon: Activity,
    sub: 'Slightly elevated — reduce size by 25%, options buying still viable.',
    riskMod: 0.75, vix: 17,
    why: 'VIX 15–20 signals moderate uncertainty. Option premiums are slightly expensive. Reduce position size by 25% and avoid chasing fast moves.',
  },
  {
    id: 'high',     label: 'High (20–25)',         color: '#f97316', icon: Activity,
    sub: 'High volatility — half size only, avoid options buying.',
    riskMod: 0.5,  vix: 22,
    why: 'VIX 20–25 means the market is fearful. Options premiums are inflated — CE/PE buying is expensive and risky. Stick to futures or equity with half size.',
  },
  {
    id: 'extreme',  label: 'Extreme (> 25)',       color: '#ef4444', icon: Activity,
    sub: 'Extreme volatility — minimum size or sit out entirely.',
    riskMod: 0.25, vix: 28,
    why: 'VIX above 25 is extreme. Stop losses widen, spreads blow out, and standard setups fail. Either sit out or take minimum size with very wide SL.',
  },
];

// ── Step 9: Instrument ────────────────────────────────────────────────────────
export const INSTRUMENTS = [
  {
    id: 'equity',      label: 'Equity Intraday',       color: '#6c63ff', icon: BarChart2,
    sub: 'Buying or selling shares — same-day exit, no expiry pressure.',
    why: 'Equity intraday is the most straightforward instrument. No premium decay, no expiry pressure. Your max loss is your stop loss distance × shares. Ideal for trend days. Beginners should master equity before touching options or futures.',
    calcPath: '/calculator',
    rules: [
      'Always set SL at entry — no exceptions',
      'Size by risk: risk ≤ 1–2% of capital',
      'Exit by 3:15 PM — never carry intraday overnight',
      'Only liquid stocks: minimum 500K shares avg daily volume',
      'If stopped out, do not re-enter same stock same session',
    ],
    avoid: ['No SL', 'Overnight holding', 'Illiquid stocks', 'Averaging down on a loss', 'Re-entering after SL hit'],
  },
  {
    id: 'ce_buy',      label: 'Call Option Buy (CE)',   color: '#16C784', icon: TrendingUp,
    sub: 'Leveraged bullish bet — defined max loss (premium paid).',
    why: 'CE buying gives you leveraged upside with defined risk. Your max loss is the premium paid. But theta decays every hour — you need price to move fast and far. Most CE buyers lose not because direction is wrong, but because the move is too slow.',
    calcPath: '/derivatives',
    rules: [
      'Only buy CE with strong bullish conviction',
      'Prefer DTE > 7 days — avoid Thursday CE buying',
      'ATM or one strike OTM only',
      'Set a time-based exit: if not in profit by 1 PM, exit',
      'Set a premium SL: exit if premium falls 30–40%',
    ],
    avoid: ['Buying CE in choppy markets', 'Buying on expiry day', 'Deep OTM strikes', 'Without clear directional thesis', 'Holding overnight'],
  },
  {
    id: 'pe_buy',      label: 'Put Option Buy (PE)',    color: '#ef4444', icon: TrendingDown,
    sub: 'Leveraged bearish bet — defined max loss (premium paid).',
    why: 'PE buying works the same as CE but bearish. Markets fall faster than they rise, so PE moves are sharp and quick. The problem: premiums inflate during falling markets, making entries expensive right when you want to buy.',
    calcPath: '/derivatives',
    rules: [
      'Only buy PE with strong bearish conviction',
      'Check IV — if already high, premium is expensive',
      'ATM or one strike OTM only',
      'Bear moves are sharp — take partial profit early',
      'Set premium SL: exit if premium falls 30–40%',
    ],
    avoid: ['Buying PE on green/bullish days', 'Holding through lunch', 'Expiry day PE buying', 'After a sharp fall already happened'],
  },
  {
    id: 'option_sell', label: 'Option Selling (Write)', color: '#f59e0b', icon: Shield,
    sub: 'Sell CE or PE to collect premium — profits from time decay and range.',
    why: 'Option selling earns money from theta decay and range-bound movement. You collect premium upfront and profit if price stays within a range. Dangerous in trending markets — losses on naked positions are theoretically unlimited.',
    calcPath: '/derivatives',
    rules: [
      'Always set SL at 2× premium collected',
      'Sell CE when price will NOT cross resistance',
      'Sell PE when price will NOT fall below support',
      'Prefer OTM strikes — not ATM',
      'Exit by 3:00 PM',
      'Never sell around events',
      'Never sell when VIX is above 20 — premium collapses unpredictably',
    ],
    avoid: ['No SL on sold positions', 'Selling ATM without clear range', 'Selling into high VIX (>20)', 'Holding through news events'],
  },
  {
    id: 'futures',     label: 'Futures (Long / Short)', color: '#8b5cf6', icon: Zap,
    sub: 'Leveraged directional trade on Nifty, BankNifty, or stock futures.',
    why: 'Futures give 1:1 delta with leverage (~10% margin). A 1% Nifty move is ~10% on your margin. No premium decay, but losses are amplified equally. Unforgiving in choppy markets — not for beginners or low-conviction setups.',
    calcPath: '/derivatives',
    rules: [
      'SL is non-negotiable — no futures without SL',
      'Margin usage: never allocate more than 20% of capital',
      'Intraday futures: exit by 3:20 PM',
      'Only in high-conviction scenarios',
      'One futures position at a time',
    ],
    avoid: ['Futures without SL', 'Using full capital as margin', 'Choppy days (whipsaw eats margin)', 'Averaging down on losing futures', 'Holding overnight without reason'],
  },
];

export const GRADE_COLOR = {
  A: '#16C784', B: '#4ecca3', C: '#f59e0b', D: '#f97316', Wait: '#8b5cf6', 'Sit Out': '#6b7280',
};

// ── Action sequence builder ───────────────────────────────────────────────────
export function buildActions(global, gap, openBehavior, setup, instrument, grade, timeSlot = null, expiry = null) {
  if (grade === 'Sit Out') return [
    'Do not place any trade today for this scenario.',
    'Open Trade History — review your last 5 similar sessions.',
    'Study the setup you thought you saw. What would confirm it?',
    'Come back tomorrow with fresh perspective.',
  ];
  const actions = [];
  actions.push(`Check Global Cues — confirm ${global.label.toLowerCase()} read (FII flow, SGX Nifty, top indices).`);

  if (expiry?.isExpiry)
    actions.push('Expiry day — avoid buying CE/PE unless the move is immediate and large. Prefer selling OTM premium or scalping with tight SL.');

  if (timeSlot?.id === 'opening')
    actions.push('Opening play — do NOT enter before 9:30 AM. Let the first 15 minutes absorb institutional orders. Watch the gap behavior first.');
  else if (timeSlot?.id === 'lunch_chop')
    actions.push('Lunch session — if not already in a trade, sit out. Low volume means setups fail more often. Resume analysis at 2 PM.');
  else if (timeSlot?.id === 'power_hour')
    actions.push('Power hour — confirm the trend direction from the morning. Hard exit by 3:15 PM. Do not open new complex positions after 3:00 PM.');

  if (gap.id === 'strong_up' || gap.id === 'strong_down') {
    actions.push(`${gap.caution} Wait 15 minutes after 9:15 AM before touching anything.`);
  } else if (gap.id === 'flat') {
    actions.push('Flat open — ORB day. Do not predict direction before 9:45 AM. Let the opening range form.');
  } else {
    actions.push(`Observe the first 15 minutes post-open — confirm ${openBehavior.label.toLowerCase()} behavior.`);
  }
  actions.push(openBehavior.signal);
  if (setup.entry)  actions.push(`Entry plan: ${setup.entry}`);
  if (setup.sl)     actions.push(`Stop Loss: ${setup.sl}`);
  if (setup.target) actions.push(`Target: ${setup.target}`);
  if (instrument.id === 'equity') {
    actions.push('Open the Risk Calculator. Enter your SL in rupees → get exact share count. Do not guess size.');
  } else if (instrument.id === 'ce_buy' || instrument.id === 'pe_buy') {
    actions.push('Check the options chain. Confirm the strike, IV, and DTE before placing the order.');
    actions.push('Set a premium SL — if premium falls 30–40% from entry, exit.');
  } else if (instrument.id === 'option_sell') {
    actions.push('Identify the range. Sell the strike outside the range, not inside it.');
    actions.push('Set SL at 2× premium collected. Enter the SL order immediately after selling.');
  } else if (instrument.id === 'futures') {
    actions.push('Confirm your SL price before entering. Futures SL is non-negotiable.');
    actions.push('Check margin usage — ensure this position is less than 20% of your total capital.');
  }
  const exitTime = '3:15 PM';
  actions.push(`Plan your exit before you enter. Know your SL, target, and time exit. Hard exit at ${exitTime}.`);
  return actions.slice(0, 9);
}

// ── Main scenario scorer ──────────────────────────────────────────────────────
export function computeScenario(global, gap, openBehavior, setup, instrument, fii = null, vix = null, expiry = null, timeSlot = null) {
  // VIX hard cap — applies before anything else
  const vixLevel   = vix ? classifyVix(vix) : null;
  const vixRiskMod = vixLevel ? vixLevel.riskMod : 1.0;

  // FII riskMod — only applies when FII contradicts global bias
  const fiiRiskMod = (() => {
    if (!fii) return 1.0;
    if (global.bias === 'bull' && fii.bias === 'sell') return 0.85;
    if (global.bias === 'bear' && fii.bias === 'buy')  return 1.1;
    return fii.riskMod;
  })();

  const expiryRiskMod   = expiry   ? expiry.riskMod   : 1.0;
  const timeSlotRiskMod = timeSlot ? timeSlot.riskMod : 1.0;

  const raw  = global.riskMod * gap.riskMod * openBehavior.riskMod * setup.riskMod
             * fiiRiskMod * vixRiskMod * expiryRiskMod * timeSlotRiskMod;
  const risk = parseFloat(Math.max(0, Math.min(1, raw)).toFixed(2));

  const contradictions = [];

  // Direction contradictions
  if (global.bias === 'bear' && instrument.id === 'ce_buy')
    contradictions.push('Bearish global backdrop + CE buying — you are trading against the macro current.');
  if (global.bias === 'bull' && instrument.id === 'pe_buy')
    contradictions.push('Bullish global backdrop + PE buying — the macro trend is against your direction.');
  if (openBehavior.id === 'reversal')
    contradictions.push('Sharp reversal at open means your original gap-direction bias is likely wrong.');
  if (openBehavior.id === 'range' && (instrument.id === 'ce_buy' || instrument.id === 'pe_buy'))
    contradictions.push('Buying directional options in a choppy market — theta decay will hurt you even if direction is eventually correct.');
  if (openBehavior.id === 'range' && instrument.id === 'futures')
    contradictions.push('Trading futures in a choppy market — whipsaw risk is high. Range days eat margin quickly.');
  if (gap.bias === 'bull' && instrument.id === 'pe_buy' && openBehavior.id === 'sustained')
    contradictions.push('Bullish gap sustaining at open — buying PE here means fighting strong intraday momentum.');
  if (gap.bias === 'bear' && instrument.id === 'ce_buy' && openBehavior.id === 'sustained')
    contradictions.push('Bearish gap sustaining at open — buying CE here means fighting strong intraday selling pressure.');
  if (setup.id === 'none')
    contradictions.push('No clear setup identified — any trade without a setup is a guess, not a plan.');
  if (setup.id === 'reversal' && instrument.id === 'futures')
    contradictions.push('Catching reversals with futures is high risk — an aggressive continuation move will stop you out fast.');

  // FII contradictions
  if (fii && fii.bias === 'sell' && global.bias === 'bull')
    contradictions.push('FII selling into a green global day — institutions are distributing into retail optimism. Breakouts are suspect.');
  if (fii && fii.bias === 'buy' && global.bias === 'bear')
    contradictions.push('FII buying despite red global cues — DIIs may be absorbing. Descent could be slower than expected, but macro is still against longs.');

  // VIX contradictions
  if (vixLevel && vix > 20 && instrument.id === 'option_sell')
    contradictions.push(`VIX at ${vix} — selling options into high volatility. Premium looks fat but risk is asymmetric. Avoid.`);
  if (vixLevel && vix > 25)
    contradictions.push(`VIX at ${vix} — extreme volatility. Standard stop losses are too tight. Minimum size only.`);

  // Expiry day contradictions
  if (expiry?.isExpiry && (instrument.id === 'ce_buy' || instrument.id === 'pe_buy'))
    contradictions.push('Expiry day + option buying — theta collapses premium even if direction is right. CE/PE buying on expiry is high-risk.');
  if (expiry?.isExpiry && instrument.id === 'futures' && openBehavior.id === 'range')
    contradictions.push('Expiry day range + futures — expiry pin risk means the range can hold all day. Futures whipsaw badly near expiry strikes.');

  // Time of day contradictions
  if (timeSlot?.id === 'lunch_chop' && (instrument.id === 'ce_buy' || instrument.id === 'pe_buy'))
    contradictions.push('Lunch session + option buying — volume is low and theta decay is active. Options bleed value even without adverse moves.');
  if (timeSlot?.id === 'lunch_chop' && instrument.id === 'futures' && setup.id !== 'none')
    contradictions.push('Lunch session + futures — low volume causes whipsaw. Spreads widen and stops get hunted. Best to wait for power hour.');
  if (timeSlot?.id === 'opening' && setup.id === 'reversal')
    contradictions.push('Opening play + reversal setup — reversals at open are usually noise before 9:30 AM. Wait for the real direction to establish.');
  if (timeSlot?.id === 'power_hour' && setup.id === 'orb_breakout')
    contradictions.push('Power hour + ORB Breakout — ORB setups should be traded by 11:30 AM. In power hour the range is stale and breakouts often fail.');
  if (timeSlot?.id === 'opening' && setup.id === 'vwap_reclaim')
    contradictions.push('Opening play + VWAP Reclaim — VWAP is not meaningful in the first 15 minutes. Let VWAP develop before using it as a signal.');

  // Setup-gap alignment
  if (setup.id === 'orb_breakout' && gap.id !== 'flat')
    contradictions.push('ORB Breakout works best on flat open days. A gap open makes the opening range less reliable.');
  if (setup.id === 'vwap_reclaim' && openBehavior.id === 'sustained')
    contradictions.push('VWAP Reclaim is a reversal signal — it conflicts with a sustained trending open.');

  const aligned =
    (global.bias === 'bull' && gap.bias === 'bull' && openBehavior.id === 'sustained' && (instrument.id === 'equity' || instrument.id === 'ce_buy')) ||
    (global.bias === 'bear' && gap.bias === 'bear' && openBehavior.id === 'sustained' && instrument.id === 'pe_buy');

  let grade;
  if (vixLevel && vix > 25 && setup.id !== 'none') {
    grade = risk >= 0.5 ? 'C' : 'D';
  } else if (setup.id === 'none' || contradictions.length >= 3) {
    grade = 'Sit Out';
  } else if (contradictions.length >= 2) {
    grade = 'Wait';
  } else if (risk >= 0.85 && aligned) {
    grade = 'A';
  } else if (risk >= 0.65) {
    grade = 'B';
  } else if (risk >= 0.35) {
    grade = 'C';
  } else if (risk > 0) {
    grade = 'D';
  } else {
    grade = 'Sit Out';
  }

  const narrative =
    grade === 'Sit Out' ? 'This combination does not offer a tradeable edge today. Capital preservation is the correct call.'
    : grade === 'Wait'  ? `${global.why} ${openBehavior.why} Wait for conditions to clarify before committing capital.`
    : `${global.why} ${gap.why} ${openBehavior.signal}`;

  // #1 — sizing text reconciles VIX adjustment explicitly
  // #4 — within-grade risk differentiation (C covers 0.35–0.65, split the language)
  const vixSuffix = vixLevel && vixLevel.label !== 'Normal'
    ? ` VIX ${vix.toFixed(0)} is ${vixLevel.label.toLowerCase()} — ${vixLevel.note}`
    : '';
  const sizing =
    grade === 'A'        ? `Full size — high conviction session.${vixSuffix}`
    : grade === 'B'      ? `Normal size — conditions are decent.${vixSuffix}`
    : grade === 'C' && risk >= 0.50 ? `Half size — conditions are mixed.${vixSuffix}`
    : grade === 'C'      ? `Quarter size — conditions are poor, don't force this.${vixSuffix}`
    : grade === 'D'      ? `Minimum size only — very low conviction.${vixSuffix}`
    : 'No trade — sit out and observe.';

  const actions = buildActions(global, gap, openBehavior, setup, instrument, grade, timeSlot, expiry);

  return {
    grade, risk, narrative, contradictions, sizing, actions,
    setupEntry: setup.entry, setupSL: setup.sl, setupTarget: setup.target,
    calcPath: instrument.calcPath, rules: instrument.rules, avoid: [...instrument.avoid],
    instrColor: instrument.color,
    instrumentWhy: instrument.why || null,
    vixNote:    vixLevel?.note    || null,
    fiiNote:    fii?.why          || null,
    expiryNote: expiry?.note      || null,
    timeNote:   timeSlot?.note    || null,
  };
}

// ── Auto-resolve best setup from known context ────────────────────────────────
// Called in live mode — returns the most likely setup given the 6 context inputs.
export function resolveSetup(openBehavior, gap, timeSlot) {
  // Lunch chop → no trade
  if (timeSlot?.id === 'lunch_chop') return SETUPS.find(s => s.id === 'none');

  const ob  = openBehavior?.id;
  const g   = gap?.id;

  if (ob === 'sustained') {
    // Trending day — pullback is the cleanest entry; breakout on strong gaps
    if (g === 'strong_up' || g === 'strong_down') return SETUPS.find(s => s.id === 'breakout');
    return SETUPS.find(s => s.id === 'pullback');
  }
  if (ob === 'reversal') {
    // Reversal at open — vwap reclaim is the safest reversal play
    return SETUPS.find(s => s.id === 'vwap_reclaim');
  }
  if (ob === 'range' || ob === 'gap_fill' || g === 'flat') {
    // Choppy/range open or gap filled → wait for ORB breakout (mirrors backend market_analysis_service.py)
    return SETUPS.find(s => s.id === 'orb_breakout');
  }
  // Fallback
  return SETUPS.find(s => s.id === 'breakout');
}

// ── Instrument lean — soft directional nudge, not a signal ───────────────────
// Returns { label, note } describing which trade type conditions favour.
// Always appends "confirm on chart" — never a hard recommendation.
export function resolveInstrumentLean(global, fii, vix, expiry, setup) {
  const vixLevel  = vix ? classifyVix(vix) : null;
  const highVix   = vixLevel && (vixLevel.label === 'High' || vixLevel.label === 'Extreme');
  const isExpiry  = expiry?.isExpiry;
  const bias      = global?.bias;
  const fiiBias   = fii?.bias;

  // Extreme VIX or no-setup → sit out
  if (vixLevel?.label === 'Extreme') return { label: 'Sit out', note: 'VIX above 25 — no new positions. Conditions too volatile for reliable setups.' };
  if (setup?.id === 'none')          return { label: 'No trade', note: 'No identifiable setup — wait for a clear pattern before looking at instruments.' };

  // Expiry day: theta makes option buying dangerous, lean sellers
  if (isExpiry) {
    if (highVix) return { label: 'Avoid options entirely', note: 'Expiry + high VIX — premium is chaotic. Sit out or scalp equity only.' };
    return { label: 'Option selling or equity scalp', note: 'Expiry day: theta collapses CE/PE fast. OTM option selling or tight equity scalps are the cleaner plays. Confirm range on chart before selling.' };
  }

  // High VIX (20–25) — options buying is expensive, lean futures or equity
  if (highVix) {
    if (bias === 'bull') return { label: 'Futures long or equity', note: 'VIX 20–25: option premiums are inflated — CE buying is expensive. Futures long or equity intraday give cleaner R:R. Confirm trend on chart.' };
    if (bias === 'bear') return { label: 'Futures short or equity short', note: 'VIX 20–25: PE premiums elevated. Futures short gives cleaner exposure than PE buying. Confirm breakdown on chart.' };
    return { label: 'Equity or futures — avoid options buying', note: 'High VIX with neutral bias — directional options are expensive and risky. Stick to equity or futures with tight SL.' };
  }

  // Normal/elevated VIX — standard lean by bias
  if (bias === 'bull' && fiiBias === 'buy')  return { label: 'CE buying or Futures long', note: 'Global + FII both bullish — conditions favour directional long. CE buying (if DTE > 7 days) or futures long. Confirm entry on chart.' };
  if (bias === 'bull')                        return { label: 'CE buying or equity long', note: 'Bullish backdrop but FII flow not confirming. CE buying or equity intraday long with reduced size. Wait for chart confirmation.' };
  if (bias === 'bear' && fiiBias === 'sell') return { label: 'PE buying or Futures short', note: 'Global + FII both bearish — conditions favour directional short. PE buying or futures short. Confirm breakdown on chart.' };
  if (bias === 'bear')                        return { label: 'PE buying or equity short', note: 'Bearish global but FII not fully aligned. PE buying or equity short with reduced size. Confirm on chart.' };

  // Neutral bias
  if (setup?.id === 'orb_breakout') return { label: 'Wait for ORB — then CE or PE', note: 'Flat/range open — direction unknown until ORB breaks. Wait for the breakout candle, then buy CE (up) or PE (down). Do not pre-position.' };
  return { label: 'Equity only — no directional options', note: 'Mixed signals — avoid leveraged instruments. Equity intraday with tight SL is the safest play. Confirm setup on chart before entry.' };
}

// ── Resolve playbook from regime API response ─────────────────────────────────
export function resolvePlaybookFromRegime(rg, ts) {
  const globalBias = rg.global_bias || 'neutral';
  const globalOpt  = GLOBAL_CONTEXTS.find(g => g.bias === globalBias) || GLOBAL_CONTEXTS[1];
  const gapOpt     = NIFTY_GAPS.find(g => g.id === rg.gap_type) || NIFTY_GAPS[2];

  // open_behavior / setup_type come directly from daily_regime (backend single source
  // of truth, market_analysis_service.py) — only re-derive as a fallback for regime
  // rows computed before these columns existed.
  const openId = rg.open_behavior || (rg.conflict ? 'mixed'
    : rg.match ? 'sustained'
    : rg.gap_type === 'flat' ? 'range'
    : 'gap_fill');
  const openOpt = OPEN_BEHAVIORS.find(o => o.id === openId) || OPEN_BEHAVIORS[2];

  const defaultSetupId = rg.setup_type || (rg.gap_type === 'flat' ? 'orb_breakout' : 'breakout');
  const setup = SETUPS.find(s => s.id === defaultSetupId) || SETUPS[0];

  const sym    = (ts.symbol       || '').toUpperCase();
  const pt     = (ts.product_type || '').toUpperCase();
  const instrId = sym.includes('CE') ? 'ce_buy'
    : sym.includes('PE') ? 'pe_buy'
    : pt.includes('FUT') ? 'futures'
    : 'equity';
  const instr = INSTRUMENTS.find(i => i.id === instrId) || INSTRUMENTS[0];

  // FII from regime if available
  const fiiNet = rg.fii_net ?? null;
  const fiiOpt = fiiNet == null ? null
    : fiiNet > 0  ? FII_CONTEXTS[0]   // buying
    : fiiNet < 0  ? FII_CONTEXTS[2]   // selling
    : FII_CONTEXTS[1];                 // neutral

  return computeScenario(globalOpt, gapOpt, openOpt, setup, instr, fiiOpt, rg.vix ?? null);
}

// ── Derive lite regime from global_indices when regime API has no data ────────
export function deriveLiteRegimeFromCues(mc) {
  const indices = mc.global_indices || [];
  if (!indices.length) return null;
  const mm = mc.market_mood;
  if (mm?.color) {
    const bias = mm.color?.toLowerCase() === '#16c784' ? 'bull' : mm.color?.toLowerCase() === '#ef4444' ? 'bear' : 'neutral';
    return { global_bias: bias, gap_type: 'flat', match: bias !== 'neutral', conflict: false };
  }
  const usEu = indices.filter(({ name = '' }) => /dow|djia|s&p|sp500|nasdaq|ftse|dax|cac|stoxx/.test(name.toLowerCase()));
  const pool  = usEu.length ? usEu : indices;
  const avg   = pool.reduce((s, i) => s + parseFloat(i.change_pct || 0), 0) / pool.length;
  const bias  = avg > 0.2 ? 'bull' : avg < -0.2 ? 'bear' : 'neutral';
  return { global_bias: bias, gap_type: 'flat', match: false, conflict: false };
}

